import React, { useState, useEffect } from 'react';
import { FaUserMd, FaPills, FaTimes, FaCalendarAlt, FaPlus, FaTrash } from 'react-icons/fa';
import tratamientoService from '../services/tratamientoService';
import { sociasApiService } from '../services/sociasApiService';
import '../styles/InventoryManagement.css';

const TratamientoModal = ({ 
    isOpen, 
    onClose, 
    pacienteId, 
    pacienteNombre, 
    onTratamientoCreated,
    tratamientoToEdit = null 
}) => {
    const [medicos, setMedicos] = useState([]);
    const [pacientes, setPacientes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    const [formData, setFormData] = useState({
        MEDICO_ID: '',
        PACIENTE_ID: pacienteId || '',
        TRATAMIENTO_FECHA_INICIO: '',
        TRATAMIENTO_FECHA_FIN: '',
        TRATAMIENTO_DESCRIPCION: ''
    });
    
    const [medicamentos, setMedicamentos] = useState([
        { id: null, nombre: '', cantidad: '', unidad: 'mg' }
    ]);
    
    // Estado para rastrear medicamentos eliminados (solo sus IDs)
    const [medicamentosEliminados, setMedicamentosEliminados] = useState([]);
    
    const [errors, setErrors] = useState({});

    // Cargar datos iniciales cuando se abre el modal
    useEffect(() => {
        if (isOpen) {
            setSubmitting(false); // Resetear el estado de envío
            loadInitialData();
            
            // Si hay un tratamiento para editar, cargar sus datos
            if (tratamientoToEdit) {
                setFormData({
                    MEDICO_ID: tratamientoToEdit.MEDICO_ID || '',
                    PACIENTE_ID: tratamientoToEdit.PACIENTE_ID || '',
                    TRATAMIENTO_FECHA_INICIO: tratamientoToEdit.TRATAMIENTO_FECHA_INICIO || '',
                    TRATAMIENTO_FECHA_FIN: tratamientoToEdit.TRATAMIENTO_FECHA_FIN || '',
                    TRATAMIENTO_DESCRIPCION: tratamientoToEdit.TRATAMIENTO_DESCRIPCION || ''
                });
                // Cargar TODOS los medicamentos si existen CON SUS IDs
                if (tratamientoToEdit.medicamentos && Array.isArray(tratamientoToEdit.medicamentos) && tratamientoToEdit.medicamentos.length > 0) {
                    // Cargar todos los medicamentos del tratamiento
                    const medicamentosEditados = tratamientoToEdit.medicamentos.map(med => ({
                        id: med.MEDICAMENTO_ID || med.medicamento_id || null, // ⚠️ IMPORTANTE: Guardar el ID
                        nombre: med.MEDICAMENTO_NOMBRE || med.medicamento_nombre || '',
                        cantidad: med.MEDICAMENTO_CANTIDAD || med.medicamento_cantidad || '',
                        unidad: med.MEDICAMENTO_UNIDAD || med.medicamento_unidad || 'mg'
                    }));
                    setMedicamentos(medicamentosEditados);
                } else if (tratamientoToEdit.MEDICAMENTO_NOMBRE) {
                    // Fallback para formato antiguo (un solo medicamento)
                    setMedicamentos([{
                        id: tratamientoToEdit.MEDICAMENTO_ID || null,
                        nombre: tratamientoToEdit.MEDICAMENTO_NOMBRE,
                        cantidad: tratamientoToEdit.MEDICAMENTO_CANTIDAD || '',
                        unidad: tratamientoToEdit.MEDICAMENTO_UNIDAD || 'mg'
                    }]);
                }
            } else {
                // Resetear formulario para nuevo tratamiento
                setFormData({
                    MEDICO_ID: '',
                    PACIENTE_ID: pacienteId || '',
                    TRATAMIENTO_FECHA_INICIO: '',
                    TRATAMIENTO_FECHA_FIN: '',
                    TRATAMIENTO_DESCRIPCION: ''
                });
                setMedicamentos([{ id: null, nombre: '', cantidad: '', unidad: 'mg' }]);
            }
            setMedicamentosEliminados([]); // Resetear medicamentos eliminados
            setErrors({});
        }
    }, [isOpen, pacienteId, tratamientoToEdit]);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            // Cargar médicos desde el servicio
            const medicosRes = await tratamientoService.getMedicosDisponibles();
            
            if (medicosRes.success) {
                setMedicos(medicosRes.data || []);
            }

            // Cargar pacientes (socias) desde la API real
            try {
                const sociasData = await sociasApiService.getSocias();
                
                const pacientesData = sociasData.map(socia => ({
                    PACIENTE_ID: socia.sociaId,
                    PACIENTE_NOMBRE: `${socia.sociaNombre || ''} ${socia.sociaApellido || ''}`.trim(),
                    PACIENTE_CEDULA: socia.sociaCedula || '',
                    PACIENTE_ESTADO: socia.sociaEstado || 'Activo'
                }));
                
                setPacientes(pacientesData);
            } catch (sociaErr) {
                console.error('Error al cargar socias desde API:', sociaErr);
                setPacientes([]);
            }

        } catch (err) {
            console.error('Error al cargar datos:', err);
        } finally {
            setLoading(false);
        }
    };

    // Validar formulario
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.MEDICO_ID) {
            newErrors.MEDICO_ID = 'Debe seleccionar un médico';
        }
        if (!formData.PACIENTE_ID) {
            newErrors.PACIENTE_ID = 'Debe seleccionar un paciente';
        }
        
        // Validar medicamentos
        medicamentos.forEach((med, index) => {
            if (!med.nombre.trim()) {
                newErrors[`medicamento_nombre_${index}`] = 'El nombre del medicamento es requerido';
            }
            if (!med.cantidad || med.cantidad <= 0) {
                newErrors[`medicamento_cantidad_${index}`] = 'La cantidad debe ser mayor a 0';
            }
        });
        
        if (!formData.TRATAMIENTO_FECHA_INICIO) {
            newErrors.TRATAMIENTO_FECHA_INICIO = 'La fecha de inicio es requerida';
        }
        if (!formData.TRATAMIENTO_FECHA_FIN) {
            newErrors.TRATAMIENTO_FECHA_FIN = 'La fecha de fin es requerida';
        }
        
        // Validar que fecha de fin sea posterior a fecha de inicio
        if (formData.TRATAMIENTO_FECHA_INICIO && formData.TRATAMIENTO_FECHA_FIN) {
            if (new Date(formData.TRATAMIENTO_FECHA_FIN) <= new Date(formData.TRATAMIENTO_FECHA_INICIO)) {
                newErrors.TRATAMIENTO_FECHA_FIN = 'La fecha de fin debe ser posterior a la fecha de inicio';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Manejar cambios en el formulario
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Limpiar error del campo
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Agregar nuevo medicamento
    const handleAddMedicamento = () => {
        setMedicamentos([...medicamentos, { id: null, nombre: '', cantidad: '', unidad: 'mg' }]);
    };

    // Eliminar medicamento
    const handleRemoveMedicamento = (index) => {
        if (medicamentos.length > 1) {
            const medicamentoAEliminar = medicamentos[index];
            
            // Si el medicamento tiene ID (existe en la BD), agregarlo a la lista de eliminados
            if (medicamentoAEliminar.id) {
                setMedicamentosEliminados(prev => [...prev, medicamentoAEliminar.id]);
            }
            
            const newMedicamentos = medicamentos.filter((_, i) => i !== index);
            setMedicamentos(newMedicamentos);
            // Limpiar errores de ese medicamento
            const newErrors = { ...errors };
            delete newErrors[`medicamento_nombre_${index}`];
            delete newErrors[`medicamento_cantidad_${index}`];
            setErrors(newErrors);
        }
    };

    // Manejar cambios en medicamentos
    const handleMedicamentoChange = (index, field, value) => {
        const newMedicamentos = [...medicamentos];
        newMedicamentos[index][field] = value;
        setMedicamentos(newMedicamentos);
        
        // Limpiar error del campo
        const errorKey = `medicamento_${field}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => ({
                ...prev,
                [errorKey]: ''
            }));
        }
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const tratamientoData = {
                SOCIA_ID: formData.PACIENTE_ID,
                MEDICO_ID: formData.MEDICO_ID,
                TRATAMIENTO_FECHAINICIO: formData.TRATAMIENTO_FECHA_INICIO,
                TRATAMIENTO_FECHAFIN: formData.TRATAMIENTO_FECHA_FIN,
                TRATAMIENTO_OBSERVACION: formData.TRATAMIENTO_DESCRIPCION,
                MEDICAMENTOS: medicamentos.map(med => {
                    const medicamentoObj = {
                        MEDICAMENTO_NOMBRE: med.nombre,
                        MEDICAMENTO_CANTIDAD: parseInt(med.cantidad) || 0,
                        MEDICAMENTO_UNIDAD: med.unidad
                    };
                    // Solo incluir el ID si existe (para actualización)
                    if (med.id) {
                        medicamentoObj.MEDICAMENTO_ID = med.id;
                    }
                    return medicamentoObj;
                })
            };

            // Si estamos editando y hay medicamentos eliminados, incluirlos
            if (tratamientoToEdit && medicamentosEliminados.length > 0) {
                tratamientoData.MEDICAMENTOS_ELIMINADOS = medicamentosEliminados;
            }

            let result;
            if (tratamientoToEdit) {
                // Actualizar tratamiento existente
                result = await tratamientoService.updateTratamiento(
                    tratamientoToEdit.TRATAMIENTO_ID, 
                    tratamientoData
                );
            } else {
                // Crear nuevo tratamiento
                result = await tratamientoService.createTratamiento(tratamientoData);
            }

            if (result.success) {
                alert(result.message);
                // Notificar al componente padre que se creó/actualizó un tratamiento
                if (onTratamientoCreated) {
                    onTratamientoCreated(result.data);
                }
                onClose();
            } else {
                alert('Error: ' + result.message);
            }
        } catch (err) {
            console.error('Error al procesar tratamiento:', err);
            alert('Error al procesar la solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    // Manejar cierre del modal
    const handleClose = () => {
        if (submitting) return; // Prevenir cierre durante envío
        onClose();
    };

    // No renderizar si el modal no está abierto
    if (!isOpen) return null;

    return (
        <div className="modal" style={{ display: 'flex', zIndex: 2000 }}>
            <div className="modal-content" style={{ maxWidth: '700px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
                <div className="modal-header">
                    <h2>
                        <FaPills style={{ marginRight: '0.5rem' }} />
                        {tratamientoToEdit ? 'Editar Tratamiento' : 'Nuevo Tratamiento'}
                        {pacienteNombre && (
                            <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#666', display: 'block' }}>
                                Para: {pacienteNombre}
                            </span>
                        )}
                    </h2>
                    <button 
                        onClick={handleClose} 
                        className="close-btn"
                        disabled={submitting}
                        title="Cerrar"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <div style={{ color: '#666' }}>
                                <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                                Cargando datos...
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="MEDICO_ID">
                                        <FaUserMd style={{ marginRight: '0.5rem' }} />
                                        Médico Responsable
                                    </label>
                                    <select
                                        id="MEDICO_ID"
                                        name="MEDICO_ID"
                                        value={formData.MEDICO_ID}
                                        onChange={handleChange}
                                        className={errors.MEDICO_ID ? 'error' : ''}
                                        disabled={tratamientoToEdit || submitting} // Solo lectura en edición
                                    >
                                        <option value="">Seleccionar médico...</option>
                                        {medicos.map(medico => (
                                            <option key={medico.MEDICO_ID} value={medico.MEDICO_ID}>
                                                {medico.MEDICO_NOMBRE} - {medico.MEDICO_ESPECIALIDAD}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.MEDICO_ID && <span className="error-message">{errors.MEDICO_ID}</span>}
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="PACIENTE_ID">Paciente</label>
                                    <select
                                        id="PACIENTE_ID"
                                        name="PACIENTE_ID"
                                        value={formData.PACIENTE_ID}
                                        onChange={handleChange}
                                        className={errors.PACIENTE_ID ? 'error' : ''}
                                        disabled={tratamientoToEdit || pacienteId || submitting} // Solo lectura en edición o si viene pre-seleccionado
                                    >
                                        <option value="">Seleccionar paciente...</option>
                                        {pacientes.map(paciente => (
                                            <option key={paciente.PACIENTE_ID} value={paciente.PACIENTE_ID}>
                                                {paciente.PACIENTE_NOMBRE} - {paciente.PACIENTE_CEDULA}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.PACIENTE_ID && <span className="error-message">{errors.PACIENTE_ID}</span>}
                                </div>
                            </div>

                            {/* Sección de Medicamentos */}
                            <div className="form-row" style={{ display: 'block' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <label style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
                                        <FaPills style={{ marginRight: '0.5rem' }} />
                                        Medicamentos
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleAddMedicamento}
                                        disabled={submitting}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            padding: '0.5rem 1rem',
                                            backgroundColor: '#2E7D32',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                            fontSize: '0.9rem',
                                            fontWeight: '500',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!submitting) e.target.style.backgroundColor = '#1B5E20';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = '#2E7D32';
                                        }}
                                    >
                                        <FaPlus /> Agregar Medicamento
                                    </button>
                                </div>

                                {medicamentos.map((medicamento, index) => (
                                    <div 
                                        key={index} 
                                        style={{ 
                                            marginBottom: '1rem',
                                            padding: '1rem',
                                            border: '2px solid #E0E0E0',
                                            borderRadius: '8px',
                                            backgroundColor: '#FAFAFA'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                            <span style={{ fontWeight: '600', color: '#666' }}>
                                                Medicamento {index + 1}
                                            </span>
                                            {medicamentos.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveMedicamento(index)}
                                                    disabled={submitting}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.3rem',
                                                        padding: '0.4rem 0.8rem',
                                                        backgroundColor: '#D32F2F',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                                        fontSize: '0.85rem',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!submitting) e.target.style.backgroundColor = '#B71C1C';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = '#D32F2F';
                                                    }}
                                                >
                                                    <FaTrash /> Eliminar
                                                </button>
                                            )}
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Nombre del Medicamento</label>
                                                <input
                                                    type="text"
                                                    value={medicamento.nombre}
                                                    onChange={(e) => handleMedicamentoChange(index, 'nombre', e.target.value)}
                                                    placeholder="Ej: Paracetamol, Ibuprofeno..."
                                                    className={errors[`medicamento_nombre_${index}`] ? 'error' : ''}
                                                    disabled={submitting}
                                                />
                                                {errors[`medicamento_nombre_${index}`] && (
                                                    <span className="error-message">{errors[`medicamento_nombre_${index}`]}</span>
                                                )}
                                            </div>
                                            
                                            <div className="form-group">
                                                <label>Cantidad</label>
                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                                                    <input
                                                        type="number"
                                                        value={medicamento.cantidad}
                                                        onChange={(e) => handleMedicamentoChange(index, 'cantidad', e.target.value)}
                                                        placeholder="0"
                                                        min="1"
                                                        className={errors[`medicamento_cantidad_${index}`] ? 'error' : ''}
                                                        disabled={submitting}
                                                        style={{ flex: '2' }}
                                                    />
                                                    <select
                                                        value={medicamento.unidad}
                                                        onChange={(e) => handleMedicamentoChange(index, 'unidad', e.target.value)}
                                                        disabled={submitting}
                                                        style={{ 
                                                            flex: '1',
                                                            padding: '0.875rem 1rem',
                                                            border: '2px solid #E0E0E0',
                                                            borderRadius: '8px',
                                                            fontSize: '1rem',
                                                            fontFamily: 'inherit',
                                                            backgroundColor: 'white',
                                                            minWidth: '70px'
                                                        }}
                                                    >
                                                        <option value="mg">mg</option>
                                                        <option value="g">g</option>
                                                    </select>
                                                </div>
                                                {errors[`medicamento_cantidad_${index}`] && (
                                                    <span className="error-message">{errors[`medicamento_cantidad_${index}`]}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="TRATAMIENTO_FECHA_INICIO">
                                        <FaCalendarAlt style={{ marginRight: '0.5rem' }} />
                                        Fecha de Inicio
                                    </label>
                                    <input
                                        type="date"
                                        id="TRATAMIENTO_FECHA_INICIO"
                                        name="TRATAMIENTO_FECHA_INICIO"
                                        value={formData.TRATAMIENTO_FECHA_INICIO}
                                        onChange={handleChange}
                                        className={errors.TRATAMIENTO_FECHA_INICIO ? 'error' : ''}
                                        disabled={submitting}
                                    />
                                    {errors.TRATAMIENTO_FECHA_INICIO && <span className="error-message">{errors.TRATAMIENTO_FECHA_INICIO}</span>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="TRATAMIENTO_FECHA_FIN">
                                        <FaCalendarAlt style={{ marginRight: '0.5rem' }} />
                                        Fecha de Fin
                                    </label>
                                    <input
                                        type="date"
                                        id="TRATAMIENTO_FECHA_FIN"
                                        name="TRATAMIENTO_FECHA_FIN"
                                        value={formData.TRATAMIENTO_FECHA_FIN}
                                        onChange={handleChange}
                                        className={errors.TRATAMIENTO_FECHA_FIN ? 'error' : ''}
                                        disabled={submitting}
                                    />
                                    {errors.TRATAMIENTO_FECHA_FIN && <span className="error-message">{errors.TRATAMIENTO_FECHA_FIN}</span>}
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="TRATAMIENTO_DESCRIPCION">Descripción e Instrucciones</label>
                                    <textarea
                                        id="TRATAMIENTO_DESCRIPCION"
                                        name="TRATAMIENTO_DESCRIPCION"
                                        value={formData.TRATAMIENTO_DESCRIPCION}
                                        onChange={handleChange}
                                        placeholder="Describe las instrucciones del tratamiento, posología, recomendaciones..."
                                        rows="4"
                                        style={{ 
                                            width: '100%', 
                                            padding: '0.875rem 1rem',
                                            border: '2px solid #E0E0E0',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            fontFamily: 'inherit',
                                            resize: 'vertical'
                                        }}
                                        disabled={submitting}
                                    />
                                </div>
                            </div>

                            {tratamientoToEdit && (
                                <div className="edit-note">
                                    <p><strong>Nota:</strong> En modo edición no se pueden modificar el médico responsable ni el paciente.</p>
                                </div>
                            )}

                            {pacienteNombre && (
                                <div className="edit-note" style={{ background: '#E8F5E8', borderColor: '#C8E6C9' }}>
                                    <p><strong>Paciente seleccionado:</strong> {pacienteNombre}</p>
                                </div>
                            )}
                        </form>
                    )}
                </div>

                <div className="modal-footer">
                    <button 
                        type="button" 
                        className="btn-secondary" 
                        onClick={handleClose}
                        disabled={submitting}
                    >
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        className="btn-primary"
                        onClick={handleSubmit}
                        disabled={loading || submitting}
                    >
                        {submitting ? (
                            <>
                                <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.5rem' }}></i>
                                {tratamientoToEdit ? 'Actualizando...' : 'Creando...'}
                            </>
                        ) : (
                            <>
                                {tratamientoToEdit ? 'Actualizar' : 'Crear'} Tratamiento
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TratamientoModal;